import { useEffect, useMemo } from 'react';
import { AlertBox, Box, FlexBox } from '@vp/swan';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import { DateFormatter, DateFormatTypes } from '@vp/om-date-formatter';
import { useSiteData } from '../../../hooks/useSiteData';
import { EmbeddableLinkCreator } from '../../shared/EmbeddableLinkCreator';
import {
  trackDesignFeedbackBannerPresented,
  trackDesignFeedbackBannerRedirectLinkClicked,
} from '../../../apis/newrelic/designFeedbackPageNewRelicTracking';
import { useOrderSummary } from '../../contexts/OrderSummaryContext';
import { useCustomerFeedbackGroup } from '../../contexts/CustomerFeedbackGroupsContext';

export const DesignFeedbackBanner = ({
  lineItemId,
}: {
  lineItemId: string;
}) => {
  const [designFeedbackMessages = {}] = useTranslations([
    'designFeedbackMessages',
  ]);

  const {
    order: { orderNumber },
  } = useOrderSummary();

  const {
    externalAppNavigation: { orderDetails: orderDetailsUrl },
    vistacartTenant,
    locale,
  } = useSiteData();

  const {
    customerFeedbackGroup: pendingDesignFeedbackGroup,
    customerFeedbackGroupExists: pendingDesignFeedbackGroupExists,
  } = useCustomerFeedbackGroup({
    lineItemId,
    requestType: 'DOCUMENT_REVIEW',
    groupState: 'AWAITING_FEEDBACK',
  });

  const {
    responseDeadlineDate: deadline = '',
    groupId = '',
    state: feedbackGroupState,
  } = pendingDesignFeedbackGroup || {};

  const designFeedbackPageUrl = `${orderDetailsUrl}?orderId=${orderNumber}&groupId=${groupId}&action=df`;

  const showBanner = pendingDesignFeedbackGroupExists;

  const newRelicTrackingData = useMemo(
    () => ({
      lineItemId,
      orderNumber,
      feedbackGroupState,
      tenant: vistacartTenant,
    }),
    [feedbackGroupState, lineItemId, orderNumber, vistacartTenant]
  );

  // Fire a New Relic metric that the banner was presented to the customer
  useEffect(() => {
    if (showBanner) {
      trackDesignFeedbackBannerPresented(groupId, newRelicTrackingData);
    }
  }, [
    groupId,
    feedbackGroupState,
    lineItemId,
    newRelicTrackingData,
    orderNumber,
    showBanner,
    vistacartTenant,
  ]);

  // On click handler for the redirect link to the design feedback page
  const handleRedirectLinkOnClick = (
    e: React.MouseEvent<HTMLAnchorElement>
  ) => {
    // Block the redirect so we can fire the NR metric
    e.preventDefault();

    // Fire a New Relic metric that the link was clicked
    trackDesignFeedbackBannerRedirectLinkClicked(groupId, newRelicTrackingData);

    // Finally, redirect to design feedback page
    window.location.assign(`${designFeedbackPageUrl}`);
  };

  if (showBanner) {
    return (
      <AlertBox skin="error">
        <FlexBox
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box textAlign="left" pr={5}>
            {designFeedbackMessages.designFeedbackTitle}{' '}
            <ReplaceTokens
              tokenizedString={designFeedbackMessages.pleaseProvideFeedback}
              substitutions={{
                DesignFeedbackLink: EmbeddableLinkCreator({
                  href: designFeedbackPageUrl,
                  onClick: handleRedirectLinkOnClick,
                  ariaDescribedBy: `product-name-${lineItemId}`, // Attach the product name to make the link unique and descriptive for assistive tech
                }),
                date: (
                  <DateFormatter
                    date={deadline}
                    formatType={DateFormatTypes.MEDIUM_FMT}
                    locale={locale}
                  />
                ),
              }}
            />
          </Box>
        </FlexBox>
      </AlertBox>
    );
  }

  return null;
};
